import type { BASE_ERROR_CODES } from "../error/codes";
import type { PrettifyDeep, UnionToIntersection } from "../types/helper";
import { capitalizeFirstLetter } from "../utils/misc";
import { getClientConfig } from "./config";
import { createDynamicPathProxy } from "./proxy";
import type {
	BetterFeatureClientPlugin,
	ClientOptions,
	InferActions,
	InferClientAPI,
	InferErrorCodes,
	IsSignal,
} from "./types";

type InferResolvedHooks<O extends ClientOptions> =
	O["plugins"] extends Array<infer Plugin>
		? Plugin extends BetterFeatureClientPlugin
			? // biome-ignore lint/suspicious/noExplicitAny: Required for type inference
				Plugin["getAtoms"] extends (fetch: any) => infer Atoms
				? // biome-ignore lint/suspicious/noExplicitAny: Required for type inference
					Atoms extends Record<string, any>
					? {
							[key in keyof Atoms as IsSignal<key> extends true
								? never
								: key extends string
									? `use${Capitalize<key>}`
									: never]: Atoms[key];
						}
					: // biome-ignore lint/complexity/noBannedTypes: Required for empty object type in conditional types
						{}
				: // biome-ignore lint/complexity/noBannedTypes: Required for empty object type in conditional types
					{}
			: // biome-ignore lint/complexity/noBannedTypes: Required for empty object type in conditional types
				{}
		: // biome-ignore lint/complexity/noBannedTypes: Required for empty object type in conditional types
			{};

export function createFeatureClient<Option extends ClientOptions>(
	options?: Option,
) {
	const {
		pluginPathMethods,
		pluginsActions,
		pluginsAtoms,
		$fetch,
		atomListeners,
		$store,
	} = getClientConfig(options);
	// biome-ignore lint/suspicious/noExplicitAny: Required for dynamic hook resolution
	const resolvedHooks: Record<string, any> = {};
	for (const [key, value] of Object.entries(pluginsAtoms)) {
		resolvedHooks[`use${capitalizeFirstLetter(key)}`] = value;
	}
	const routes = {
		...pluginsActions,
		...resolvedHooks,
		$fetch,
		$store,
	};
	const proxy = createDynamicPathProxy(
		routes,
		$fetch,
		pluginPathMethods,
		pluginsAtoms,
		atomListeners,
	);
	type ClientAPI = InferClientAPI<Option>;

	return proxy as UnionToIntersection<InferResolvedHooks<Option>> &
		ClientAPI &
		InferActions<Option> & {
			$fetch: typeof $fetch;
			$store: typeof $store;
			// biome-ignore lint/complexity/noBannedTypes: Required for empty object type
			$Infer: {};
			$ERROR_CODES: PrettifyDeep<
				InferErrorCodes<Option> & typeof BASE_ERROR_CODES
			>;
		};
}
